/**
 * Notes as a slide-over — the tenant profile's variant of the same feed.
 *
 * The profile is already a modal with five tabs; notes are the thing you want
 * open WHILE reading the payments or the move-out, so they get a panel over the
 * content rather than a sixth tab you have to leave to consult them.
 *
 * Rendered inside the profile's own frame (not a portal) so it is clipped to
 * the panel and cannot escape over the page. That means it also has to take
 * Escape away from the dialog: without this, one keypress would close the whole
 * profile instead of the drawer sitting on top of it.
 */
import { useEffect } from "react";
import { Lock, StickyNote, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NoteScope } from "@/hooks/v2/useEntityNotes";
import { NotesThread } from "./NotesThread";

export function NotesDrawer({
  open,
  onClose,
  scope,
  title = "Notes",
  emptyHint,
  className,
}: {
  open: boolean;
  onClose: () => void;
  scope: NoteScope;
  title?: string;
  emptyHint?: string;
  className?: string;
}) {
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      // Capture phase + stopPropagation: the surrounding Radix dialog listens
      // for Escape too, and the top-most layer is the one that should close.
      e.stopPropagation();
      onClose();
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Dimming the profile behind it says "this is on top" and gives a
          click-anywhere exit, which a bare panel does not. */}
      <div
        className="absolute inset-0 z-30 bg-[rgba(9,15,28,0.18)]"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={title}
        className={cn(
          "absolute bottom-3 right-3 top-3 z-40 flex w-[min(430px,calc(100%-24px))] flex-col",
          "overflow-hidden rounded-2xl border border-border-subtle bg-white shadow-2xl",
          "duration-200 animate-in slide-in-from-right-4 fade-in-0",
          className
        )}
      >
        <header className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
          <StickyNote className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h2 className="text-[15px] font-bold text-heading">{title}</h2>
          <span className="inline-flex items-center gap-1 rounded-full bg-sand px-2 py-0.5 text-[11px] font-semibold text-body-soft">
            <Lock className="h-2.5 w-2.5" />
            Internal
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notes"
            className="ml-auto grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-sand hover:text-body"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <NotesThread scope={scope} emptyHint={emptyHint} />
        </div>
      </aside>
    </>
  );
}
